import { useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { Waiting } from "./Shell";
import { SectionHead } from "./SectionHead";

// The Floor Manager — autonomy dials (with the gate ceiling made visible), escalations, the spend
// pause, and market balance. Not the Recruiter; no outreach; no schedules / re-run / refresh charge.
const DIAL_LABEL = { suggest_only: "Suggest only", ask_first: "Ask me first", act_notify: "Act & notify" };

export default function TheFloorManager() {
  const { authedFetch } = useAuth();
  const { waiting, loaded } = useContext(Waiting);
  const onYou = waiting["floor_manager"] || 0;
  const [tab, setTab] = useState("dials");
  const [v, setV] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    authedFetch("/floor-manager/section").then(setV).catch((e) => setErr(String(e)));
  }, [authedFetch]);
  useEffect(load, [load]);

  if (err) return <div className="gk-err" data-testid="fm-error">{err}</div>;
  if (!v) return <div className="loading" data-testid="fm-loading">Loading…</div>;

  const counts = { dials: v.dials.dials.length, escalations: v.escalations.open_count,
                   pause: 0, market: v.market.total };
  const tabs = [["dials", "Autonomy"], ["escalations", "Escalations"], ["pause", "Pause"], ["market", "Market balance"]];
  const pfm = v.pause?.floor_manager || {};
  const spendPaused = pfm.spend_paused ?? pfm.paused ?? false;

  return (
    <div data-testid="the-floor-manager">
      <SectionHead eyebrow="THE FLOOR MANAGER · THE FLOOR" testid="fm"
        headline={spendPaused
          ? "Spend is paused — nothing new is being claimed."
          : !loaded
            ? `${counts.escalations} open on the floor.`
            : onYou > 0
              ? `${onYou} ${onYou === 1 ? "escalation is" : "escalations are"} waiting on you.`
              : "Nothing is stuck on the floor."}
        lede={loaded && counts.escalations !== onYou
          ? `${counts.escalations} listed in the Escalations tab — the difference is the ones past their deadline, which the Command Center files under stuck, plus any labelled fixtures.`
          : null}>
        <p className="gk-constraint" data-testid="fm-constraint">{v.constraint}</p>
      </SectionHead>
      <div role="tablist" className="tabs desk-tabs">
        {tabs.map(([id, label]) => (
          <button key={id} role="tab" className={`desk-tab ${tab === id ? "on" : ""}`}
                  data-testid={`fm-tab-${id}`} aria-selected={tab === id} onClick={() => setTab(id)}>
            {label}<span className="tab-count">{counts[id]}</span>
          </button>
        ))}
      </div>
      {tab === "dials" && <Dials d={v.dials} authedFetch={authedFetch} reload={load} />}
      {tab === "escalations" && <Escalations e={v.escalations} authedFetch={authedFetch} reload={load} />}
      {tab === "pause" && <Pause p={v.pause} authedFetch={authedFetch} reload={load} />}
      {tab === "market" && <Market m={v.market} />}
    </div>
  );
}

function Dials({ d, authedFetch, reload }) {
  return (
    <div className="gk-panel" data-testid="fm-dials">
      <p className="gk-recorded-note">{d.note}</p>
      <table className="gk-table" data-testid="fm-dials-table">
        <thead><tr><th>agent</th><th>task</th><th>setting</th><th></th></tr></thead>
        <tbody>
          {d.dials.map((row) => (
            <DialRow key={`${row.agent}.${row.task_type}`} row={row} authedFetch={authedFetch} reload={reload} />
          ))}
        </tbody>
      </table>
      <div className="gk-b9-block">
        <h3>Per-agent summary <span className="gk-tag">read only</span></h3>
        <ul className="gk-b9-list" data-testid="fm-rollup">
          {Object.entries(d.rollup).map(([agent, dials]) => (
            <li key={agent}>{agent}: {Object.entries(dials).map(([k, n]) => `${DIAL_LABEL[k]} ×${n}`).join(" · ")}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function DialRow({ row, authedFetch, reload }) {
  const [preview, setPreview] = useState(null);   // {from,to,direction,requires_confirm}
  const [busy, setBusy] = useState(false);

  async function choose(dial) {
    if (dial === row.effective) return;
    const p = await authedFetch("/floor-manager/dial/preview", { method: "POST",
      body: { agent: row.agent, task_type: row.task_type, dial } });
    if (!p.allowed) return;
    if (p.requires_confirm) { setPreview(p); return; }   // widening → confirm; narrowing applies direct
    await apply(dial);
  }
  async function apply(dial) {
    setBusy(true);
    await authedFetch("/floor-manager/dial", { method: "POST",
      body: { agent: row.agent, task_type: row.task_type, dial } });
    setPreview(null); setBusy(false); reload();
  }

  return (
    <tr data-testid={`fm-dial-${row.agent}-${row.task_type}`}>
      <td className="mono">{row.agent}</td>
      <td>{row.label}<div className="gk-muted mono">{row.task_type}</div></td>
      <td>
        <div className="fm-dial-set">
          {["suggest_only", "ask_first", "act_notify"].map((opt) => {
            const unavailable = opt === "act_notify" && row.act_notify_unavailable;
            const on = row.effective === opt;
            return (
              <button key={opt} className={`fm-dial-opt ${on ? "on" : ""} ${unavailable ? "unavailable" : ""}`}
                      data-testid={`fm-dial-opt-${row.agent}-${row.task_type}-${opt}`}
                      disabled={unavailable || busy} title={unavailable ? row.ceiling_reason : ""}
                      onClick={() => !unavailable && choose(opt)}>
                {DIAL_LABEL[opt]}{unavailable && <span className="fm-lock" data-testid={`fm-ceiling-${row.task_type}`}>ceiling</span>}
              </button>
            );
          })}
        </div>
      </td>
      <td>
        {preview && (
          <span className="st-edit-box st-preview" data-testid={`fm-confirm-${row.agent}-${row.task_type}`}>
            <span className="st-diff">{DIAL_LABEL[preview.from]} <strong>→</strong> {DIAL_LABEL[preview.to]}</span>
            <span className="gk-muted">widens autonomy</span>
            <button className="act act-approve" data-testid={`fm-proceed-${row.agent}-${row.task_type}`}
                    disabled={busy} onClick={() => apply(preview.to)}>Proceed</button>
            <button className="linkbtn" onClick={() => setPreview(null)}>Cancel</button>
          </span>
        )}
      </td>
    </tr>
  );
}

function Escalations({ e, authedFetch, reload }) {
  return (
    <div className="gk-panel" data-testid="fm-escalations">
      {e.fixtures_included && (
        <div className="gk-fixture-banner" data-testid="fm-fixture-banner">
          Showing labelled fixtures — dev review only, never promotable, production never seeded.
        </div>
      )}
      <div className="gk-b9-block">
        <h3>Dismissed as false positive, by task <span className="gk-tag">threshold evidence</span></h3>
        <p className="gk-muted">{e.note}</p>
        <ul className="gk-b9-list" data-testid="fm-fp-counts">
          {Object.keys(e.false_positive_counts).length === 0
            ? <li className="gk-muted">none</li>
            : Object.entries(e.false_positive_counts).map(([k, n]) => (
                <li key={k} className={n >= 3 ? "gk-warn-text" : ""}>{k}: {n}{n >= 3 ? " — firing too eagerly" : ""}</li>
              ))}
        </ul>
      </div>
      {e.truncated && (
        <p className="gk-muted" data-testid="fm-esc-truncated">
          Showing {e.shown_open} of {e.total_open}
        </p>
      )}
      {e.open.length === 0
        ? <div className="empty-state"><p>No open escalations.</p></div>
        : e.open.map((esc) => <EscCard key={esc.id} esc={esc} resolutions={e.resolutions} authedFetch={authedFetch} reload={reload} />)}
    </div>
  );
}

// A refusal always reaches the person who clicked — never an empty box and never [object Object].
const errText = (e, fallback) =>
  (typeof e === "string" ? e : e && (e.detail || e.message)) || fallback;

function EscCard({ esc, resolutions, authedFetch, reload }) {
  const [resolution, setResolution] = useState("");
  const [reasoning, setReasoning] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [rerunErr, setRerunErr] = useState("");
  const [outcome, setOutcome] = useState("");
  const [dupMsg, setDupMsg] = useState("");

  async function resolve() {
    if (!resolution) { setMsg("Choose an outcome first — nothing is pre-selected."); return; }
    if (!reasoning.trim()) { setMsg("A reason is required — it goes on the record."); return; }
    setBusy(true);
    try {
      const r = await authedFetch("/floor-manager/escalation/resolve", { method: "POST",
        body: { escalation_id: esc.id, resolution, reasoning } });
      setMsg(r.persisted ? `Resolved: ${resolution}.` : `Accepted (fixture, not persisted): ${resolution}.`);
      if (r.persisted) reload();
    } catch (e2) { setMsg(String(e2)); }
    setBusy(false);
  }

  async function askRerun() {
    setRerunErr(""); setBusy(true);
    try {
      setPreview(await authedFetch("/floor-manager/escalation/redrive/preview", { method: "POST",
        body: { escalation_id: esc.id } }));
    } catch (e) { setRerunErr(errText(e, "Could not prepare a re-run for this document.")); }
    setBusy(false);
  }

  async function confirmRerun() {
    setRerunErr(""); setBusy(true);
    try {
      const r = await authedFetch("/floor-manager/escalation/redrive", { method: "POST",
        body: { escalation_id: esc.id } });
      setPreview(null);
      setMsg(`Re-reading ${r.filename}. This stays open until a mark or an honest no lands.`);
      reload();
    } catch (e) { setRerunErr(errText(e, "Could not re-run this document.")); }
    setBusy(false);
  }

  async function resolveDuplicate() {
    if (!outcome) { setDupMsg("Choose an outcome first — nothing is pre-selected."); return; }
    if (!reasoning.trim()) { setDupMsg("A reason is required — it goes on the record."); return; }
    setBusy(true);
    try {
      const r = await authedFetch("/floor-manager/escalation/duplicate-hold", { method: "POST",
        body: { escalation_id: esc.id, outcome, reasoning } });
      setDupMsg(r.message);
      reload();
    } catch (e2) { setDupMsg(errText(e2, "Could not resolve this hold.")); }
    setBusy(false);
  }

  return (
    <div className="held-card" data-testid={`fm-esc-${esc.id}`}>
      <div className="held-head">
        <span className="held-kind">{esc.reason}</span>
        <span className="held-field"><strong>{esc.agent}</strong> · {esc.task_type}</span>
        {esc.deadline && <span className="status-pill sp-needs-human">due {String(esc.deadline).slice(0, 10)}</span>}
      </div>
      <p className="held-why" data-testid={`fm-esc-detail-${esc.id}`}>{esc.handed_over_text}</p>

      {esc.duplicate_hold && (
        <div className="action-bar" data-testid={`fm-esc-duplicate-${esc.id}`}>
          <select className="intent-input" style={{ maxWidth: 380 }} value={outcome}
                  data-testid={`fm-dup-outcome-${esc.id}`} onChange={(ev) => setOutcome(ev.target.value)}>
            <option value="" disabled>choose an outcome…</option>
            {(esc.outcomes || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button className="act act-approve" data-testid={`fm-dup-resolve-${esc.id}`}
                  disabled={busy} onClick={resolveDuplicate}>
            {busy ? "Working…" : "Resolve and read it onward"}
          </button>
          {dupMsg && <span className="gk-msg" data-testid={`fm-dup-msg-${esc.id}`}>{dupMsg}</span>}
        </div>
      )}

      {esc.redrivable && (
        <div className="action-bar">
          {!preview ? (
            <button className="act" data-testid={`fm-esc-rerun-${esc.id}`} disabled={busy}
                    onClick={askRerun}>Re-run this document</button>
          ) : (
            <div className="redrive-confirm" data-testid={`fm-esc-rerun-confirm-${esc.id}`}>
              <div className="redrive-preview" data-testid={`fm-esc-rerun-preview-${esc.id}`}>
                <div>{preview.filename} · {preview.label}</div>
                <ul className="gk-b9-list">
                  {(preview.will || []).map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
              <div className="redrive-actions">
                <button className="act act-approve" data-testid={`fm-esc-rerun-proceed-${esc.id}`}
                        disabled={busy} onClick={confirmRerun}>{busy ? "Re-running…" : "Proceed"}</button>
                <button className="linkbtn" data-testid={`fm-esc-rerun-cancel-${esc.id}`}
                        disabled={busy} onClick={() => setPreview(null)}>Cancel</button>
              </div>
            </div>
          )}
          {rerunErr && <span className="lm-alarm-err" data-testid={`fm-esc-rerun-err-${esc.id}`}>{rerunErr}</span>}
        </div>
      )}

      <div className="action-bar">
        <select className="intent-input" style={{ maxWidth: 240 }} value={resolution}
                data-testid={`fm-resolution-${esc.id}`} onChange={(ev) => setResolution(ev.target.value)}>
          <option value="" disabled>choose an outcome…</option>
          {resolutions.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <input className="intent-input" placeholder="reason on the record" value={reasoning}
               data-testid={`fm-reason-${esc.id}`} onChange={(ev) => setReasoning(ev.target.value)} />
        <button className="act" data-testid={`fm-resolve-${esc.id}`} disabled={busy} onClick={resolve}>Resolve</button>
      </div>
      {msg && <div className="gk-msg" data-testid={`fm-msg-${esc.id}`}>{msg}</div>}
    </div>
  );
}

const usd = (micros) => `$${((micros || 0) / 1_000_000).toFixed(2)}`;

function reconciliationWords(r) {
  if (!r) return "PENDING — not yet reconciled against the provider console.";
  if (typeof r === "string") return r;
  if (r.words) return r.words;
  return "PENDING — not yet reconciled against the provider console.";
}

function Pause({ p, authedFetch, reload }) {
  const fm = p.floor_manager || {};
  const paused = fm.spend_paused ?? fm.paused ?? false;
  const c = fm.ceilings || {};
  const bu = fm.billed_unknown_today || {};
  const breaches = fm.recent_breaches || [];
  const [reasoning, setReasoning] = useState("");
  const [msg, setMsg] = useState("");

  async function clear() {
    if (!reasoning.trim()) { setMsg("Clearing a pause is a decision — it needs a reason."); return; }
    try {
      await authedFetch("/floor-manager/pause/clear", { method: "POST", body: { reasoning } });
      setMsg("Pause cleared, on the record."); reload();
    } catch (e) { setMsg(String(e)); }
  }

  return (
    <div className="gk-panel" data-testid="fm-pause">
      <div className="gk-recorded-note">
        Spend pause: <strong className={paused ? "gk-warn-text" : ""} data-testid="fm-pause-state">{paused ? "PAUSED — new work not claimed" : "not paused"}</strong>.
        Clearing a pause is a decision and takes a reason, appended and never silent.
      </div>

      <div className="fm-pause-grid" data-testid="fm-pause-detail">
        <div className="fm-pause-item">
          <div className="fm-pause-k">per-document ceiling</div>
          <div className="fm-pause-v mono">{usd(c.per_document_micros)}</div>
        </div>
        <div className="fm-pause-item">
          <div className="fm-pause-k">daily ceiling · used today · headroom</div>
          <div className="fm-pause-v mono" data-testid="fm-pause-daily">
            {usd(c.daily_micros)} · {usd(c.daily_micros_used_today)} used ·{" "}
            <span className={c.daily_headroom_micros === 0 ? "gk-warn-text" : ""}>{usd(c.daily_headroom_micros)} left</span>
          </div>
        </div>
        {c.monthly_micros != null && (
          <div className="fm-pause-item">
            <div className="fm-pause-k">monthly ceiling · used · headroom</div>
            <div className="fm-pause-v mono">
              {usd(c.monthly_micros)} · {usd(c.monthly_micros_used)} used · {usd(c.monthly_headroom_micros)} left
            </div>
          </div>
        )}
        <div className="fm-pause-item fm-pause-full">
          <div className="fm-pause-k">billed_unknown today <span className="gk-tag">shown separately, never folded in</span></div>
          <div className="fm-pause-v mono" data-testid="fm-pause-billed-unknown">
            {(bu.calls || 0)} attempt{(bu.calls || 0) === 1 ? "" : "s"} · {usd(bu.spend_micros)} — issued but unpriced (timeout/transport), accrued at a conservative estimate against the ceilings until reconciliation.
          </div>
        </div>
        <div className="fm-pause-item fm-pause-full">
          <div className="fm-pause-k">provider reconciliation</div>
          <div className="fm-pause-v" data-testid="fm-pause-reconciliation">{reconciliationWords(fm.provider_reconciliation)}</div>
        </div>
      </div>

      {breaches.length > 0 && (
        <div className="gk-b9-block">
          <h3>Recent ceiling breaches <span className="gk-tag">read only</span></h3>
          <ul className="gk-b9-list" data-testid="fm-pause-breaches">
            {breaches.map((b, i) => (
              <li key={i} className="gk-warn-text">{b.reason} · {b.subject_id} · {b.state}{b.opened_at ? ` · ${String(b.opened_at).slice(0, 10)}` : ""}</li>
            ))}
          </ul>
        </div>
      )}

      {paused && (
        <div className="action-bar">
          <input className="intent-input" placeholder="reason to clear the pause" value={reasoning}
                 data-testid="fm-pause-reason" onChange={(e) => setReasoning(e.target.value)} />
          <button className="act act-approve" data-testid="fm-pause-clear" onClick={clear}>Clear pause</button>
        </div>
      )}
      {msg && <div className="gk-msg" data-testid="fm-pause-msg">{msg}</div>}
    </div>
  );
}

function Market({ m }) {
  return (
    <div className="gk-panel" data-testid="fm-market">
      <div className="stat-row">
        {Object.entries(m.counts).map(([role, n]) => (
          <div className="stat" key={role} data-testid={`fm-seat-${role}`}>
            <div className="stat-val">{n}</div>
            <div className="stat-label">{role}</div>
          </div>
        ))}
        <div className="stat" data-testid="fm-seat-total"><div className="stat-val">{m.total}</div><div className="stat-label">total</div></div>
      </div>
      <div className="gk-recorded-note" data-testid="fm-verdict">
        <strong>{m.verdict.display}.</strong> {m.verdict.reason}.
      </div>
      <p className="gk-muted" data-testid="fm-market-boundary">{m.boundary}</p>
    </div>
  );
}
